import { useCallback, useMemo } from 'react';
import { NOTIFICATIONS_NUMBER_OF_PLACEHOLDERS } from 'models/Notification';
import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import { PaginatedData } from 'models/PaginatedData';
import { getHasElementsInArray } from 'utils';

export default function useNotificationsCommon<T>(
  notificationsData: InfiniteData<PaginatedData<T>>,
  isFetching: boolean,
  isFetchingNextPage: boolean,
  hasNextPage: boolean,
  fetchNextPage: (
    options?: FetchNextPageOptions,
  ) => Promise<InfiniteQueryObserverResult<PaginatedData<T>, unknown>>,
) {
  const notifications = useMemo(
    () => notificationsData?.pages.flatMap(({ items }) => items) ?? [],
    [notificationsData],
  );

  const hasData = useMemo(
    () => getHasElementsInArray(notifications),
    [notifications],
  );

  const numberOfPlaceholders = useMemo(
    () =>
      NOTIFICATIONS_NUMBER_OF_PLACEHOLDERS[
        !hasData ? 'INITIAL_LOADING_COUNT' : 'PAGINATED_LOADING_COUNT'
      ],
    [hasData],
  );

  const onNotificationsScroll = useCallback(() => {
    if (isFetching || isFetchingNextPage) return;
    if (hasNextPage) fetchNextPage();
  }, [isFetching, isFetchingNextPage, hasNextPage, fetchNextPage]);

  return { notifications, numberOfPlaceholders, onNotificationsScroll };
}
